import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@tabler/icons-react';
import { Input } from '@core-ui/Components/ui/input';
import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@core-ui/Components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import { PaginationControls } from '@ui/shared/paginationControls';
import ContactInfoDialog from './ContactInfoDialog';
import WaveMini from '@features/voicemail/components/WaveMini';
import PlayIcon from '@features/voicemail/components/PlayIcon';
import useDashboardApi, { type UseDashboardApiReturn } from '../hooks/useDashboardApi';
import type { SessionDetailsCall, SessionRecordings, CallRecording, LoadingState, ContactInfo } from '../types/dashboard.types';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { Flame } from 'lucide-react';

interface CallsTableProps {
  calls: SessionDetailsCall[];
  searchQuery: string;
  dispositionFilter: string;
  agentFilter: string;
  actionFilter: string;
  uniqueDispositions: string[];
  uniqueAgents: Array<{ id: string; name: string }>;
  uniqueActions: string[];
  onSearchChange: (query: string) => void;
  onDispositionChange: (disposition: string) => void;
  onAgentChange: (agent: string) => void;
  onActionChange: (action: string) => void;
  onLoadRecording: (callId: string) => Promise<void>;
  onCloseRecording: () => void;
  selectedRecordings: SessionRecordings | null;
  recordingsLoadingState: LoadingState;
}

function getDispositionLabel (key: string, t: (k: string, fallback: string) => string): string {
  return t(`dashboard.dispositions.${key}`, key);
}

const DISPOSITION_COLORS: Record<string, string> = {
  // Positive outcomes (green)
  set_appointment: 'bg-green-500/20 text-green-400 border-green-500/30',
  SA: 'bg-green-500/20 text-green-400 border-green-500/30',
  // Voicemail (orange)
  left_vm: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  LVM: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  // No answer (red) / bad number (gray)
  no_answer: 'bg-red-500/20 text-red-400 border-red-500/30',
  NA: 'bg-red-500/20 text-red-400 border-red-500/30',
  bad_number_busy_signal: 'bg-red-500/20 text-red-400 border-red-500/30',
  'BN/BS': 'bg-red-500/20 text-red-400 border-red-500/30',
  wrong_number: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  WN: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  // Transferred (blue)
  transferred_without_disposition: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  TND: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  // Gatekeeper (purple)
  gatekeeper_conversation: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  GC: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  // Decision maker (cyan)
  decision_maker_conversation: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  DMC: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  // Opt out / refusal (red)
  opt_out: 'bg-red-500/20 text-red-400 border-red-500/30',
  OO: 'bg-red-500/20 text-red-400 border-red-500/30',
  refuse_transfer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  RTT: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  phone_refusal: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  PR: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  // Phone Tree No Connection (purple)
  PTNC: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  // No Longer with Company (pink)
  NLC: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  // Referred to another contact (indigo)
  RAC: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
};

function getActionLabel (key: string, t: (k: string, fallback: string) => string): string {
  return t(`dashboard.actions.${key}`, key);
}

const ACTION_COLORS: Record<string, string> = {
  transferred: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  answered: 'bg-green-500/20 text-green-400 border-green-500/30',
  voicemail: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  not_answered: 'bg-red-500/20 text-red-400 border-red-500/30',
  cut_off: 'bg-red-500/20 text-red-400 border-red-500/30',
  transfer_error: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function getInitials (name: string): string {
  if (name === '' || name === null || name === undefined) {
    return '??';
  }
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function formatDate (
  dateValue: string | { $date: string } | null | undefined,
  lang: string
): string {
  if (dateValue == null) {
    return '-';
  }

  const localeLower = (lang ?? '').toLowerCase();
  const locale =
      localeLower.startsWith('es') ? 'es-ES' :
        localeLower.startsWith('en') ? 'en-US' :
          'en-US';

  let dateString: string;
  if (typeof dateValue === 'object' && dateValue.$date !== '') {
    dateString = dateValue.$date;
  } else if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    return '-';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${timeStr}, ${dateStr}`;
}

// Speed options for playback
const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];

function parseDuration (duration?: string): number {
  if (duration == null || duration === '') {
    return 0;
  }
  const parsed = parseFloat(duration);
  return isNaN(parsed) ? 0 : parsed;
}

// Inline Recording Player
function InlineRecordingPlayer ({
  recordings,
  isLoading,
  isError,
  onClose,
  onRetry,
}: {
  recordings: SessionRecordings | null;
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeRecording, setActiveRecording] = useState<'sdr' | 'agent'>(
    recordings?.sdrCallRecording != null ? 'sdr' : 'agent'
  );
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const [audioError, setAudioError] = useState(false);

  const currentRecording: CallRecording | undefined = activeRecording === 'sdr' ? recordings?.sdrCallRecording : recordings?.agentCallRecording;
  const recordingUrl = currentRecording?.recordingUrl;

  const [duration, setDuration] = useState(() => parseDuration(currentRecording?.duration));

  // Reset active recording when recordings change
  useEffect(() => {
    setActiveRecording(recordings?.sdrCallRecording != null ? 'sdr' : 'agent');
  }, [recordings]);

  // Update duration when the active recording changes
  useEffect(() => {
    setDuration(parseDuration(currentRecording?.duration));
  }, [currentRecording?.duration]);

  useEffect(() => {
    if (recordingUrl == null) {
      return;
    }

    setAudioError(false);
    const audio = new Audio(recordingUrl);
    audioRef.current = audio;
    audio.playbackRate = playbackSpeed;

    const onLoadedMetadata = (): void => {
      setDuration(audio.duration);
    };
    const onTimeUpdate = (): void => {
      if (audio.duration > 0) {
        setProgress(audio.currentTime / audio.duration);
      }
    };
    const onEnded = (): void => {
      setIsPlaying(false);
      setProgress(0);
    };
    const onError = (): void => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
      audio.src = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingUrl]);

  const togglePlay = (): void => {
    if (audioRef.current == null) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setAudioError(true);
        setIsPlaying(false);
      });
    }
  };

  const cycleSpeed = (): void => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    const newSpeed = PLAYBACK_SPEEDS[nextIndex];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current != null) {
      audioRef.current.playbackRate = newSpeed;
    }
  };

  const handleDownload = (): void => {
    if (recordingUrl == null) {
      return;
    }
    window.open(recordingUrl, '_blank');
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentTime = progress * duration;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-xs">{t('dashboard.callsTable.loading')}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-500">{t('dashboard.callsTable.error')}</span>
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={onRetry}
        >
          {t('dashboard.callsTable.retry')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          title={t('dashboard.callsTable.close')}
        >
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </Button>
      </div>
    );
  }

  if (audioError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-500">{t('dashboard.callsTable.audioUnavailable')}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          title={t('dashboard.callsTable.close')}
        >
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </Button>
      </div>
    );
  }

  if (recordings == null || (recordings.sdrCallRecording == null && recordings.agentCallRecording == null)) {
    return <span className="text-muted-foreground text-sm">{t('dashboard.callsTable.noRecording')}</span>;
  }

  const hasBoth = recordings.sdrCallRecording != null && recordings.agentCallRecording != null;

  return (
    <div className="flex items-center gap-2">
      {/* Play/Pause */}
      <Button
        variant="ghost"
        size="icon"
        className={`h-9 w-9 rounded-full border ${isPlaying ? 'bg-sky-100 text-sky-700 border-sky-300' : 'text-primary hover:text-primary/80 border-border'}`}
        onClick={togglePlay}
        title={isPlaying ? t('dashboard.callsTable.pause') : t('dashboard.callsTable.play')}
      >
        <PlayIcon playing={isPlaying} />
      </Button>

      {/* Waveform */}
      <WaveMini progress={progress} animated={isPlaying} />

      {/* Current Time / Total Duration */}
      <span className="text-xs text-muted-foreground min-w-[70px] tabular-nums">
        {duration > 0 ? `${formatDuration(currentTime)} / ${formatDuration(duration)}` : '--:--'}
      </span>

      {/* Speed Control */}
      <Button
        variant="outline"
        size="sm"
        className="h-6 px-2 text-xs min-w-[40px] font-medium"
        onClick={cycleSpeed}
        title={t('dashboard.callsTable.changeSpeed')}
      >
        {playbackSpeed}x
      </Button>

      {/* Recording Type Toggle (SDR/Agent) */}
      {hasBoth ? (
        <div className="flex h-7 rounded-md border border-border overflow-hidden">
          <button
            type="button"
            className={`px-2 text-xs font-medium transition-colors ${activeRecording === 'sdr' ? 'bg-sky-100 text-sky-700' : 'text-muted-foreground hover:bg-muted/60'}`}
            onClick={() => {
              setActiveRecording('sdr');
              setIsPlaying(false);
              setProgress(0);
            }}
          >
            {t('dashboard.callsTable.sdr')}
          </button>
          <button
            type="button"
            className={`px-2 text-xs font-medium border-l border-border transition-colors ${activeRecording === 'agent' ? 'bg-sky-100 text-sky-700' : 'text-muted-foreground hover:bg-muted/60'}`}
            onClick={() => {
              setActiveRecording('agent');
              setIsPlaying(false);
              setProgress(0);
            }}
          >
            {t('dashboard.callsTable.agent')}
          </button>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          {recordings.sdrCallRecording != null ? t('dashboard.callsTable.sdr') : t('dashboard.callsTable.agent')}
        </span>
      )}

      {/* Download Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={handleDownload}
        title={t('dashboard.callsTable.downloadRecording')}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
        </svg>
      </Button>

      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-muted-foreground hover:text-foreground"
        onClick={onClose}
        title={t('dashboard.callsTable.closePlayer')}
      >
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </Button>
    </div>
  );
}

export default function CallsTable ({
  calls,
  searchQuery,
  dispositionFilter,
  agentFilter,
  actionFilter,
  uniqueDispositions,
  uniqueAgents,
  uniqueActions,
  onSearchChange,
  onDispositionChange,
  onAgentChange,
  onActionChange,
  onLoadRecording,
  onCloseRecording,
  selectedRecordings,
  recordingsLoadingState,
}: CallsTableProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);
  const [contactLoadingState, setContactLoadingState] = useState<LoadingState>('IDLE');
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const { getContactByIdAsync }: UseDashboardApiReturn = useDashboardApi();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  // Paginate calls
  const paginatedCalls = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return calls.slice(startIndex, startIndex + rowsPerPage);
  }, [calls, currentPage, rowsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dispositionFilter, agentFilter, actionFilter]);

  const handlePlayClick = async (callId: string): Promise<void> => {
    if (activeCallId === callId) {
      // If clicking the same call, just toggle off
      setActiveCallId(null);
      return;
    }
    setActiveCallId(callId);
    await onLoadRecording(callId);
  };

  const handleCloseRecording = (): void => {
    setActiveCallId(null);
    onCloseRecording();
  };

  const handleContactClick = async (contactId: string): Promise<void> => {
    setIsContactDialogOpen(true);
    setContactLoadingState('LOADING');
    setSelectedContact(null);

    const response = await getContactByIdAsync(contactId);

    if (response.success) {
      setSelectedContact(response.data);
      setContactLoadingState('SUCCESS');
    } else {
      setContactLoadingState('ERROR');
    }
  };

  const hasActiveFilters = searchQuery !== '' || dispositionFilter !== 'all' || uniqueActions.length > 0 && actionFilter !== 'all' || uniqueAgents.length > 0 && agentFilter !== 'all';

  const clearFilters = (): void => {
    onSearchChange('');
    onDispositionChange('all');
    onActionChange('all');
    onAgentChange('all');
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border/60">
        <h3 className="font-semibold text-foreground">{t('dashboard.callsTable.title')}</h3>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 p-4 border-b border-border/60">
        <div className="relative flex-1 max-w-sm">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-qa-id="search-bar"
            value={searchQuery}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            placeholder={t('dashboard.callsTable.searchPlaceholder')}
            className="pl-10 h-9 bg-background"
          />
        </div>

        <Select value={dispositionFilter} onValueChange={onDispositionChange}>
          <SelectTrigger data-qa-id="dispositions-button" className="w-[180px] h-9 bg-background">
            <SelectValue placeholder={t('dashboard.callsTable.allDispositions')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('dashboard.callsTable.allDispositions')}</SelectItem>
            {uniqueDispositions.map((d) => (
              <SelectItem data-qa-id={getDispositionLabel(d, t)} key={d} value={d}>
                {getDispositionLabel(d, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {uniqueActions.length > 0 && (
          <Select value={actionFilter} onValueChange={onActionChange}>
            <SelectTrigger data-qa-id="actions-button" className="w-[160px] h-9 bg-background">
              <SelectValue placeholder={t('dashboard.callsTable.allActions')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.callsTable.allActions')}</SelectItem>
              {uniqueActions.map((action) => (
                <SelectItem data-qa-id={getActionLabel(action, t)} key={action} value={action}>
                  {getActionLabel(action, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {uniqueAgents.length > 0 && (
          <Select value={agentFilter} onValueChange={onAgentChange}>
            <SelectTrigger data-qa-id="agents-button" className="w-[160px] h-9 bg-background">
              <SelectValue placeholder={t('dashboard.callsTable.allAgents')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.callsTable.allAgents')}</SelectItem>
              {uniqueAgents.map((agent) => (
                <SelectItem data-qa-id={agent.name} key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-muted-foreground hover:text-foreground h-9"
          >
            {t('dashboard.filterBar.clearAll')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-auto">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="border-b border-border/60 hover:bg-transparent">
              <TableHead data-qa-id="contact-header" className="text-muted-foreground font-medium">{t('dashboard.callsTable.contact')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.disposition')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.action')}</TableHead>
              {uniqueAgents.length > 0 && (
                <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.agent')}</TableHead>
              )}
              {featureFlags.callRecordings && (
                <TableHead data-qa-id="recordings-header" className="text-muted-foreground font-medium">{t('dashboard.callsTable.recording')}</TableHead>
              )}
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.callsTable.date')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCalls.length === 0 ? (
              <TableRow data-qa-id="call-row-0">
                <TableCell
                  data-qa-id="call-not-found"
                  colSpan={uniqueAgents.length > 0 ? 6 : 5}
                  className="text-center py-12 text-muted-foreground"
                >
                  {t('dashboard.callsTable.noCallsFound')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedCalls.map((call, r) => (
                <TableRow
                  data-qa-id={`call-row-${r}`}
                  key={call.callId}
                  className="border-b border-border/60 hover:bg-muted/30 transition-colors"
                >
                  {/* Contact */}
                  <TableCell data-qa-id={`contact-cell-${r}`} className="py-3">
                    <button
                      type="button"
                      className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left"
                      onClick={() => {
                        void handleContactClick(call.contactId);
                      }}
                    >
                      <Avatar className="h-9 w-9 shadow">
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                          {getInitials(call.contactName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-primary hover:underline cursor-pointer">
                        {call.contactName !== '' ? call.contactName : t('dashboard.callsTable.unknownContact')}
                      </span>
                      {call.flagged && (
                        <Flame className="size-4 md:size-5 shrink-0 text-amber-500/40 fill-current" />
                      )}
                    </button>
                  </TableCell>

                  {/* Disposition */}
                  <TableCell data-qa-id={`disposition-cell-${r}`}>
                    {call.disposition != null ? (
                      <Badge
                        variant="outline"
                        className={`text-xs px-2 py-0.5 ${
                          DISPOSITION_COLORS[call.disposition] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}
                      >
                        {getDispositionLabel(call.disposition, t)}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>

                  {/* Action */}
                  <TableCell data-qa-id={`action-cell-${r}`}>
                    <Badge
                      variant="outline"
                      className={`text-xs px-2 py-0.5 ${
                        ACTION_COLORS[call.action] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}
                    >
                      {getActionLabel(call.action, t)}
                    </Badge>
                  </TableCell>

                  {/* Agent (if available) */}
                  {uniqueAgents.length > 0 && (
                    <TableCell data-qa-id={`agent-cell-${r}`} className="text-muted-foreground">
                      {call.agentName ?? '-'}
                    </TableCell>
                  )}

                  {/* Recording */}
                  {featureFlags.callRecordings && (
                    <TableCell>
                      {call.existRecordings ? (
                        activeCallId === call.callId ? (
                          <InlineRecordingPlayer
                            key={call.callId}
                            recordings={selectedRecordings}
                            isLoading={recordingsLoadingState === 'LOADING'}
                            isError={recordingsLoadingState === 'ERROR'}
                            onClose={handleCloseRecording}
                            onRetry={() => {
                              void onLoadRecording(call.callId);
                            }}
                          />
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-2 text-primary hover:text-primary/80"
                            onClick={() => {
                              void handlePlayClick(call.callId);
                            }}
                          >
                            <PlayIcon playing={false} />
                            <span className="text-xs">{t('dashboard.callsTable.play')}</span>
                          </Button>
                        )
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  )}

                  {/* Date */}
                  <TableCell className="text-muted-foreground">
                    {formatDate(call.createdAt, i18n.language)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <PaginationControls
        mode="client"
        totalItems={calls.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setCurrentPage(1);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        className="border-t border-border/60"
      />

      {/* Contact Info Dialog */}
      <ContactInfoDialog
        contact={selectedContact}
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        loadingState={contactLoadingState}
      />
    </div>
  );
}
